import { phrase } from '../messages/phrase.js';
import { EARLY_UPGRADE_DISCOUNT, MAX_UPGRADE_LEVEL, originBiome, SHINE_FROM_LEVEL, SMITH_REACH, UPGRADE_BASE_COST, UPGRADE_COST_GROWTH, UPGRADE_LEVEL_COLORS, UPGRADE_STEPS, upgradeMaterialOf, } from '../data/upgrades.js';
/**
 * Equipment upgrade at the smith (ADR 0013): what a piece is worth at a given
 * level, what the next attempt costs, and whether it succeeds. Pure; the
 * server applies the outcome, the client previews it with the same functions.
 */
/** Whether the smith can work on it at all: equipment that carries stats. */
export function isUpgradeable(template) {
    if (template.kind !== 'equipment' || template.cosmetic)
        return false;
    if (template.slot === 'pet' || template.slot === 'mount')
        return false;
    return Object.values(template.bonus).some((value) => value !== 0);
}
/** The attempt that takes a piece from `level` to the next, or undefined at the top. */
export function nextUpgradeStep(level) {
    if (!Number.isInteger(level) || level < 0 || level >= MAX_UPGRADE_LEVEL)
        return undefined;
    return UPGRADE_STEPS[level];
}
/** Cumulated bonus of a level, in percent; 0 at +0. */
export function upgradeBonusPercent(level) {
    if (level <= 0)
        return 0;
    return UPGRADE_STEPS[Math.min(level, MAX_UPGRADE_LEVEL) - 1]?.bonus ?? 0;
}
/**
 * The stats a piece gives at this level: its template's, times (1 + bonus),
 * health and defence to the point, dps to the tenth. Both are scaled as whole numbers
 * (dps in tenths) so a half rounds up the same way on every machine. The
 * small pieces gain little, the big ones a lot; the template is never touched.
 */
export function effectiveBonus(bonus, level) {
    const factor = 100 + upgradeBonusPercent(level);
    return {
        health: Math.round((bonus.health * factor) / 100),
        defence: Math.round((bonus.defence * factor) / 100),
        dps: Math.round((Math.round(bonus.dps * 10) * factor) / 100) / 10,
    };
}
/**
 * What the next attempt consumes: the materials of the piece's own biome and
 * its price in gold, `baseCost × 1.35^level reached`, with a discount on
 * the first meadow and forest steps; undefined when the
 * piece cannot go further (not upgradeable, or already +10).
 */
export function upgradeCost(template, level) {
    const step = nextUpgradeStep(level);
    if (step === undefined || !isUpgradeable(template))
        return undefined;
    const biome = originBiome(template);
    const earlyDiscount = step.level <= EARLY_UPGRADE_DISCOUNT.throughLevel &&
        EARLY_UPGRADE_DISCOUNT.biomes.includes(biome)
        ? EARLY_UPGRADE_DISCOUNT.multiplier
        : 1;
    return {
        templateId: upgradeMaterialOf(biome, step.material),
        quantity: step.quantity,
        gold: Math.round(UPGRADE_BASE_COST[biome] * UPGRADE_COST_GROWTH ** step.level * earlyDiscount),
    };
}
/**
 * One attempt from `level`. A failure costs the materials and nothing else:
 * the piece never goes down and is never destroyed.
 */
export function rollUpgrade(level, rng) {
    const step = nextUpgradeStep(level);
    if (step === undefined)
        return undefined;
    const roll = rng();
    const success = roll * 100 < step.chance;
    return { success, level: success ? step.level : level, chance: step.chance, roll };
}
/**
 * Whether a player standing at `at` can talk to one of these smiths: within
 * `SMITH_REACH` tiles, diagonals included (Chebyshev), so the whole square
 * around the anvil counts.
 */
export function withinSmithReach(at, smiths) {
    return smiths.some((smith) => Math.max(Math.abs(smith.x - at.x), Math.abs(smith.y - at.y)) <= SMITH_REACH);
}
/** « Épée de givre +7 », or the bare name at +0: how a piece is named everywhere. */
export function upgradedName(name, level) {
    return level > 0 ? `${name} +${String(level)}` : name;
}
/** What the whole server reads when someone reaches +8 or more. */
export function upgradeAnnouncement(playerName, itemName, level) {
    if (level <= 0)
        return phrase("{player} vient d'obtenir {item}", { player: playerName, item: itemName });
    return phrase("{player} vient d'obtenir {item} +{level}", {
        player: playerName,
        item: itemName,
        level,
    });
}
/** The colour a piece shines with at this level, or undefined below +3. */
export function upgradeColor(level) {
    if (level < SHINE_FROM_LEVEL)
        return undefined;
    return UPGRADE_LEVEL_COLORS[Math.min(level, MAX_UPGRADE_LEVEL)];
}
/**
 * How many stars twinkle at once on a piece: one at +3 and +4, then one more
 * every two levels, four at +9 and +10. More would bury a 16 px piece.
 */
export function upgradeStarCount(level) {
    if (level < SHINE_FROM_LEVEL)
        return 0;
    return Math.min(4, Math.ceil((Math.min(level, MAX_UPGRADE_LEVEL) - 2) / 2));
}
//# sourceMappingURL=upgrade.js.map